import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import deviceService from '../utils/services/DeviceService';

/**
 * React hook for device operations with caching and real-time updates
 */
export const useDeviceService = () => {
  const { currentUser } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const unsubscribeRef = useRef(null);

  // Initialize service
  useEffect(() => {
    deviceService.initialize();
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  /**
   * Get all devices for the current user
   */
  const getUserDevices = useCallback(async (useCache = true) => {
    if (!currentUser?.uid) {
      setDevices([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const userDevices = await deviceService.getUserDevices(currentUser.uid, useCache);
      setDevices(userDevices);
      return userDevices;
    } catch (err) {
      setError(err.message);
      
      // Check if it's an index error and log instructions
      if (err.message && err.message.includes('index')) {
        deviceService.logIndexInstructions();
      }
      
      return [];
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  /**
   * Get device statistics
   */
  const getDeviceStats = useCallback(async (useCache = true) => {
    if (!currentUser?.uid) {
      setStats(null);
      return null;
    }

    try {
      const deviceStats = await deviceService.getDeviceStats(currentUser.uid, useCache);
      setStats(deviceStats);
      return deviceStats;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching device stats:', err);
      return null;
    }
  }, [currentUser?.uid]);

  /**
   * Create a custom token for device authentication
   */
  const createCustomToken = useCallback(async (devicePin) => {
    setError(null);

    try {
      const result = await deviceService.createCustomToken(devicePin);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Create a new device
   */
  const createDevice = useCallback(async (deviceData) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    setError(null);

    try {
      const newDevice = await deviceService.createDevice({
        ...deviceData,
        uid: currentUser.uid
      });
      
      // Refresh devices list
      await getUserDevices(false);
      
      return newDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUser?.uid, getUserDevices]);

  /**
   * Update an existing device
   */
  const updateDevice = useCallback(async (deviceId, updateData) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.updateDevice(deviceId, updateData);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a device
   */
  const deleteDevice = useCallback(async (deviceId) => {
    setError(null);

    try {
      await deviceService.deleteDevice(deviceId);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.filter(device => device.key !== deviceId)
      );
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Ping a device (update last access)
   */
  const pingDevice = useCallback(async (deviceId, version = null) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.pingDevice(deviceId, version);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get online devices
   */
  const getOnlineDevices = useCallback(async (useCache = true) => {
    if (!currentUser?.uid) return [];

    try {
      return await deviceService.getOnlineDevices(currentUser.uid, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [currentUser?.uid]);

  /**
   * Get offline devices
   */
  const getOfflineDevices = useCallback(async (useCache = true) => {
    if (!currentUser?.uid) return [];

    try {
      return await deviceService.getOfflineDevices(currentUser.uid, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [currentUser?.uid]);

  /**
   * Get devices by status
   */
  const getDevicesByStatus = useCallback(async (status, useCache = true) => {
    if (!currentUser?.uid) return [];

    try {
      return await deviceService.getDevicesByStatus(currentUser.uid, status, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [currentUser?.uid]);

  /**
   * Get devices assigned to a screen
   */
  const getDevicesByScreen = useCallback(async (screenId, useCache = true) => {
    if (!currentUser?.uid) return [];

    try {
      return await deviceService.getDevicesByScreen(currentUser.uid, screenId, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [currentUser?.uid]);

  /**
   * Assign screen to device
   */
  const assignScreenToDevice = useCallback(async (deviceId, screenId) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.assignScreenToDevice(deviceId, screenId);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Remove screen from device
   */
  const removeScreenFromDevice = useCallback(async (deviceId) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.removeScreenFromDevice(deviceId);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Set up real-time listener for devices
   */
  const subscribeToDevices = useCallback(() => {
    if (!currentUser?.uid) return;

    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    try {
      unsubscribeRef.current = deviceService.subscribeToUserDevices(
        currentUser.uid,
        (updatedDevices, changes) => {
          setDevices(updatedDevices);
          
          // Log changes for debugging
          if (changes.length > 0) {
            console.log('Device changes:', changes);
          }
        }
      );
    } catch (err) {
      setError(err.message);
      console.error('Error setting up device listener:', err);
    }
  }, [currentUser?.uid]);

  /**
   * Set up real-time listener for a specific device
   * @param {string} deviceId - Device document ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  const subscribeToDevice = useCallback((deviceId, callback) => {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      return deviceService.subscribeToDevice(deviceId, callback);
    } catch (err) {
      setError(err.message);
      console.error('Error setting up device listener:', err);
      return () => {}; // Return empty cleanup function
    }
  }, []);

  /**
   * Set up real-time listener for screen assignment changes on a specific device
   * @param {string} deviceId - Device document ID
   * @param {Function} callback - Callback function called when screen assignment changes
   * @returns {Function} Unsubscribe function
   */
  const subscribeToDeviceScreenAssignment = useCallback((deviceId, callback) => {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      return deviceService.subscribeToDeviceScreenAssignment(deviceId, callback);
    } catch (err) {
      setError(err.message);
      console.error('Error setting up device screen assignment listener:', err);
      return () => {}; // Return empty cleanup function
    }
  }, []);

  /**
   * Set up real-time listener for screen assignment changes across all user devices
   * @param {Function} callback - Callback function called when any device's screen assignment changes
   * @returns {Function} Unsubscribe function
   */
  const subscribeToUserDeviceScreenAssignments = useCallback((callback) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      return deviceService.subscribeToUserDeviceScreenAssignments(currentUser.uid, callback);
    } catch (err) {
      setError(err.message);
      console.error('Error setting up user device screen assignment listener:', err);
      return () => {}; // Return empty cleanup function
    }
  }, [currentUser?.uid]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    deviceService.clearCache();
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    return deviceService.getCacheStats();
  }, []);

  /**
   * Log index creation instructions
   */
  const logIndexInstructions = useCallback(() => {
    deviceService.logIndexInstructions();
  }, []);

  /**
   * Debug method to check raw data
   */
  const debugGetAllDevices = useCallback(async (uid = null) => {
    try {
      return await deviceService.debugGetAllDevices(uid);
    } catch (error) {
      console.error('Debug error:', error);
      return [];
    }
  }, []);

  /**
   * Batch update devices
   */
  const batchUpdateDevices = useCallback(async (operations) => {
    setError(null);

    try {
      await deviceService.batchUpdateDevices(operations);
      
      // Refresh devices list
      await getUserDevices(false);
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [getUserDevices]);

  /**
   * Clean up old offline devices
   */
  const cleanupOldOfflineDevices = useCallback(async (daysThreshold = 30) => {
    if (!currentUser?.uid) return 0;

    setError(null);

    try {
      const cleanedCount = await deviceService.cleanupOldOfflineDevices(currentUser.uid, daysThreshold);
      
      if (cleanedCount > 0) {
        // Refresh devices list
        await getUserDevices(false);
      }
      
      return cleanedCount;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUser?.uid, getUserDevices]);

  /**
   * Delete a schedule from a device
   */
  const deleteSchedule = useCallback(async (deviceId, schedule) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.deleteSchedule(deviceId, schedule);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update device schedules (replace all schedules)
   */
  const updateDeviceSchedules = useCallback(async (deviceId, schedules) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.updateDeviceSchedules(deviceId, schedules);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Add a new schedule to a device
   */
  const addSchedule = useCallback(async (deviceId, newSchedule) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.addSchedule(deviceId, newSchedule);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update an existing schedule in a device
   */
  const updateSchedule = useCallback(async (deviceId, scheduleId, updatedSchedule) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.updateSchedule(deviceId, scheduleId, updatedSchedule);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get schedules for a specific device
   */
  const getDeviceSchedules = useCallback(async (deviceId, useCache = true) => {
    try {
      return await deviceService.getDeviceSchedules(deviceId, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  /**
   * Subscribe to device online status changes for all user devices
   */
  const subscribeToDeviceOnlineStatus = useCallback((callback) => {
    if (!currentUser?.uid) {
      return () => {};
    }

    return deviceService.subscribeToDeviceOnlineStatus(currentUser.uid, callback);
  }, [currentUser?.uid]);

  /**
   * Subscribe to online status changes for a specific device
   */
  const subscribeToDeviceOnlineStatusChange = useCallback((deviceId, callback) => {
    if (!deviceId) {
      return () => {};
    }

    return deviceService.subscribeToDeviceOnlineStatusChange(deviceId, callback);
  }, []);

  // Auto-subscribe to devices when user changes
  useEffect(() => {
    if (currentUser?.uid) {
      // Initial load
      getUserDevices();
      getDeviceStats();
      
      // Set up real-time listener
      subscribeToDevices();
    } else {
      setDevices([]);
      setStats(null);
    }
  }, [currentUser?.uid, getUserDevices, getDeviceStats, subscribeToDevices]);

  return {
    // State
    devices,
    loading,
    error,
    stats,
    
    // Actions
    getUserDevices,
    getDeviceStats,
    createCustomToken,
    createDevice,
    updateDevice,
    deleteDevice,
    pingDevice,
    getOnlineDevices,
    getOfflineDevices,
    getDevicesByStatus,
    getDevicesByScreen,
    assignScreenToDevice,
    removeScreenFromDevice,
    subscribeToDevices,
    subscribeToDevice,
    subscribeToDeviceScreenAssignment,
    subscribeToUserDeviceScreenAssignments,
    subscribeToDeviceOnlineStatus,
    subscribeToDeviceOnlineStatusChange,
    clearError,
    clearCache,
    getCacheStats,
    batchUpdateDevices,
    cleanupOldOfflineDevices,
    
    // Schedule Management
    deleteSchedule,
    updateDeviceSchedules,
    addSchedule,
    updateSchedule,
    getDeviceSchedules,
    
    // Computed values
    onlineDevices: devices.filter(device => device.isOnline),
    offlineDevices: devices.filter(device => !device.isOnline),
    activeDevices: devices.filter(device => device.status === 'active'),
    assignedDevices: devices.filter(device => device.hasScreen),
    unassignedDevices: devices.filter(device => !device.hasScreen),
    
    // Utility functions
    logIndexInstructions,
    debugGetAllDevices
  };
};

export default useDeviceService; 